import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { useStore } from 'hooks/useStore'
import { useSettleCheckout } from 'hooks/use-settle-checkout'
import { getPendingCheckouts, removePendingCheckout } from 'utils/checkout-recovery'

// Layouts remount on navigation; recover once per page load (and again on reconnecting), not on every route change
let recoveredForUserId: string | null = null
// Who is signed in now. Signing out and in happens without a reload, so a recovery still running for the previous
// member must stop: under another member's session their checkout reads as NOT_FOUND and would be dropped.
let signedInUserId: string | null = null

/**
 * Finishes the signed-in user's ticket purchases that a refresh, a closed tab or a dropped connection cut off after
 * they paid in Stripe Checkout: the purchase is completed on their next visit, without asking them to do anything.
 */
export function usePendingCheckoutRecovery() {
	const { user } = useStore()
	const userId = user?.userId
	const settle = useSettleCheckout()
	const settleRef = useRef(settle)
	settleRef.current = settle

	useEffect(() => {
		signedInUserId = userId ?? null
		if (!userId) {
			// Signing out doesn't reload the page: recover again when someone signs back in, even the same member
			recoveredForUserId = null
			return
		}

		const recover = async (skipRedirectSession: boolean) => {
			// On load, the redirect checkout (?step=Complete&session_id=…) finishes its own session on that page. If
			// that couldn't finish it, reconnecting picks it up here like any other.
			const redirectSessionId = skipRedirectSession
				? new URLSearchParams(window.location.search).get('session_id')
				: null
			// Nothing here touches component state, so it runs to the end even if the layout unmounts meanwhile
			for (const checkout of getPendingCheckouts(userId)) {
				if (checkout.sessionId === redirectSessionId) continue
				if (signedInUserId !== userId) return

				const outcome = await settleRef.current(checkout, { retryDelaysMs: [2000, 4000] })
				// Signed out meanwhile: keep the checkout for its member's next visit, and tell no one else about it
				if (signedInUserId !== userId) return
				if (outcome === 'unresolved') continue

				removePendingCheckout(checkout.sessionId)
				if (outcome === 'completed') {
					toast.success(`Your tickets for ${checkout.eventName} are confirmed.`)
				} else if (outcome === 'failed') {
					// The purchase can be refused while the payment is still on hold, so don't promise there was no charge
					toast.error(
						`We couldn't complete your ticket purchase for ${checkout.eventName}. If your card shows a pending payment, the host will resolve it. Please contact the host.`
					)
				}
			}
		}

		if (recoveredForUserId !== userId) {
			recoveredForUserId = userId
			void recover(true)
		}
		const recoverOnReconnect = () => void recover(false)
		window.addEventListener('online', recoverOnReconnect)
		return () => window.removeEventListener('online', recoverOnReconnect)
	}, [userId])
}
